import type { Company, UserCredentials, UserInfo } from '@entities/user';
import { BusServices } from '@main/bus-layer/bus-services';
import type { BusEvent } from '@main/utils/bus-event-promisifier';
import { BusEventsPromisifier } from '@main/utils/bus-event-promisifier';
import sleep from '@main/utils/sleep';

export interface BusLoginFulfilled {
  credentials: UserCredentials;
  currentUser: UserInfo;
  company: Company;
}

export type BusCurrentUser = UserInfo;

export type BusCurrentUserCompany = Company;

export interface BusLoginByEmailAndPasswordRejected {
  error: Error;
}

export class BusAuthentication {
  static async loginByEmailAndPassword(
    busIFrame: HTMLIFrameElement,
    email: string,
    password: string,
  ): Promise<BusLoginFulfilled> {
    const event: BusEvent = {
      type: 'AUTH.EMAIL_AND_PASSWORD',
      payload: {
        email,
        password,
        platform: 'salesforce',
      },
    };

    const loginResult =
      await BusEventsPromisifier.promisifySyncEvent<BusLoginFulfilled>({
        event,
        fulfilled: 'AUTH.EMAIL_AND_PASSWORD_FULFILLED',
        rejected: 'AUTH.EMAIL_AND_PASSWORD_REJECTED',
        target: busIFrame,
      });

    await BusServices.init(busIFrame);
    await BusServices.storeAgentList(busIFrame);

    return loginResult;
  }

  static async loginBySso(
    busIFrame: HTMLIFrameElement,
    code: string,
    redirectUrl: string,
  ): Promise<BusLoginFulfilled> {
    const event: BusEvent = {
      type: 'AUTH.SSO',
      payload: {
        code,
        redirectUrl,
        platform: 'salesforce',
      },
    };

    const loginResult =
      await BusEventsPromisifier.promisifySyncEvent<BusLoginFulfilled>({
        event,
        fulfilled: 'AUTH.SSO_FULFILLED',
        rejected: 'AUTH.SSO_REJECTED',
        target: busIFrame,
      });

    await BusServices.init(busIFrame);
    await BusServices.storeAgentList(busIFrame);

    return loginResult;
  }

  static async unifiedLogin(
    busIFrame: HTMLIFrameElement,
    token: string,
    refreshToken: string,
  ): Promise<BusLoginFulfilled> {
    // Update credentials in Bus session
    const event: BusEvent = {
      type: 'AUTH.UPDATE_CREDENTIALS',
      payload: {
        token: token,
        refreshToken: refreshToken,
      },
    };
    await BusEventsPromisifier.promisifyFireAndForgetEvent({
      event,
      target: busIFrame,
    });

    sleep(1000); // Wait for the credentials to be updated in the Bus session

    // Get user info from bus session
    const fetchCurrentuserEvent: BusEvent = {
      type: 'API.FETCH_CURRENT_USER',
    };
    const currentUser =
      await BusEventsPromisifier.promisifySyncEvent<BusCurrentUser>({
        event: fetchCurrentuserEvent,
        fulfilled: 'API.FETCH_CURRENT_USER_FULFILLED',
        rejected: 'API.FETCH_CURRENT_USER_REJECTED',
        target: busIFrame,
      });

    // Get company details from bus session
    const fetchCurrentcompanyEvent: BusEvent = {
      type: 'API.FETCH_CURRENT_USER_COMPANY',
    };

    const currentCompany =
      await BusEventsPromisifier.promisifySyncEvent<BusCurrentUserCompany>({
        event: fetchCurrentcompanyEvent,
        fulfilled: 'API.FETCH_CURRENT_USER_COMPANY_FULFILLED',
        rejected: 'API.FETCH_CURRENT_USER_COMPANY_REJECTED',
        target: busIFrame,
      });

    await BusServices.init(busIFrame);
    await BusServices.storeAgentList(busIFrame);

    return {
      company: currentCompany,
      currentUser: currentUser,
      credentials: {
        idToken: token,
        refreshToken: refreshToken,
      },
    };
  }

  static async logout(busIFrame: HTMLIFrameElement): Promise<void> {
    const event: BusEvent = {
      type: 'AUTH.SIGN_OUT',
      payload: {
        platform: 'salesforce',
      },
    };

    await BusEventsPromisifier.promisifySyncEvent({
      event,
      fulfilled: 'AUTH.SIGN_OUT_FULFILLED',
      rejected: 'AUTH.SIGN_OUT_REJECTED',
      target: busIFrame,
    });

    await BusServices.destroy(busIFrame);
  }
}
